import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** First non-empty line, so a multi-line body collapses to a single preview line. */
function firstNonEmptyLine(text) {
  for (const line of String(text ?? "").split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed) return trimmed;
  }
  return "";
}

// react-markdown v10 passes a `node` prop to custom components — strip it so it never hits the DOM.
const InlineSpan = ({ node, ...props }) => <span {...props} />;
// Render links as plain text (drop `href`) — the row is a <button>, so a nested <a> is invalid.
const InlineLink = ({ node, href, ...props }) => <span {...props} />;
const InlineStrong = ({ node, ...props }) => <strong {...props} />;
const InlineEm = ({ node, ...props }) => <em {...props} />;
const InlineDel = ({ node, ...props }) => <del {...props} />;
const InlineCode = ({ node, className, ...props }) => (
  <code
    className="rounded px-1 font-mono text-[0.9em]"
    style={{ backgroundColor: "color-mix(in oklch, currentColor 12%, transparent)" }}
    {...props}
  />
);

/**
 * Every block renders as a span so the preview stays on one truncatable line, and
 * links/images/rules are dropped — the row is a <button>, so nested interactive elements
 * would be invalid HTML.
 */
const PREVIEW_COMPONENTS = {
  p: InlineSpan,
  h1: InlineSpan,
  h2: InlineSpan,
  h3: InlineSpan,
  h4: InlineSpan,
  h5: InlineSpan,
  h6: InlineSpan,
  ul: InlineSpan,
  ol: InlineSpan,
  li: InlineSpan,
  blockquote: InlineSpan,
  pre: InlineSpan,
  a: InlineLink,
  strong: InlineStrong,
  em: InlineEm,
  del: InlineDel,
  code: InlineCode,
  img: () => null,
  hr: () => null,
  input: () => null,
  br: () => " ",
};

/**
 * Inline markdown for thread-list previews — renders the first line of a message body
 * (headings, **bold**, `code`, ~~strike~~) as compact inline text matching the chat body.
 * Memoized so the live wait-time clock re-rendering each row doesn't re-parse unchanged previews.
 *
 * @param {{ text: string }} props
 */
export const ThreadPreviewMarkdown = memo(function ThreadPreviewMarkdown({ text }) {
  const line = firstNonEmptyLine(text);
  if (!line) return null;
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={PREVIEW_COMPONENTS}>
      {line}
    </ReactMarkdown>
  );
});
